import { Injectable } from '@angular/core';
import { Http, Response, Headers, RequestOptions } from '@angular/http';
import { Observable, Observer } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import { ReplaySubject } from 'rxjs/';

import { BasicError, Error401, Error403, Error404, Error409, Error422 } from '../models/error/error';
import { Authentication } from '../models/authentication';
import { LocalStorageService } from 'angular-2-local-storage';


@Injectable()

/**
 * This service is the basic communication service which requests data or delivers data
 * to the api. This service also handles the authenticationError that may occur if the users
 * token gets obsolete.
 */
export class CommunicationService {
  private authentication: Authentication;
  private baseUrl: String = 'https://tyt.johannesschobel.com';
  // private baseUrl: String = 'https://sivantos.johannesschobel.com';
  private refreshPath: String = '/api/v1/auth/refresh';
  private refreshReplay: ReplaySubject<any> = null;

  /**
   * Basic constructor of CommunicationService
   */
  constructor(private http: Http, private localStorage: LocalStorageService) {
    // Read cookie and check if there is any token there
    const token = (this.localStorage.get('token')) ? this.localStorage.get('token').toString() : null;
    const uname = (this.localStorage.get('username')) ? this.localStorage.get('username').toString() : null;

    if (token !== undefined && token !== null && token !== '') {
      const auth: Authentication = new Authentication('', '', token);

      if (uname !== undefined && uname !== null && uname !== '') {
        auth.setUsername(uname);
      }

      this.authentication = auth;
    }
  }

  /**
   * Returns the current authentication
   * @return authentication object of communicationService
   */
  public getAuthentication(): Authentication {
    return this.authentication;
  }

  /**
   * This function sets the authentication service
   * @param authentication authentication object
   */
  public setAuthentication(authentication: Authentication): void {
    this.authentication = authentication;

    // Set token and username into cookie. Password shouldn't be saved into any cookie
    // this.cookie.set('token', this.authentication.getToken().toString());
    // this.cookie.set('username', this.authentication.getUsername().toString());

    this.localStorage.set('token', this.authentication.getToken().toString());
    this.localStorage.set('username', this.authentication.getUsername().toString());
  }

  /**
   * This function unsets the current authentication. This unset is just deleting the cookie
   */
  public unsetAuthentication(): void {
    // this.cookie.delete('token');
    // this.cookie.delete('username');

    this.localStorage.remove('token');
    this.localStorage.remove('username');
  }

  /**
   * This function creates the Authentication header and alters the given header.
   * @param headers headers of the http call
   */
  private createAuthorizationHeader(headers: Headers) {
    if (this.authentication !== undefined && this.authentication !== null) {
      if (this.authentication.getToken()) {
        headers.append('Authorization', 'Bearer ' + this.authentication.getToken());
      }
    }
  }


  /**
   * Relaying request to authentication
   */
  public refreshToken(): ReplaySubject<any> {
    if (this.refreshReplay !== null) {
      return this.refreshReplay;
    }

    const refreshReplay: ReplaySubject<any> = new ReplaySubject(1);
    this.refreshReplay = refreshReplay;

    const request = this.post(this.refreshPath, {});
    request.subscribe(data => {
      if (data instanceof BasicError) {
        // Refreshing failed
        console.log('----------AuthenticationService---------: Failed to refresh token.');
        refreshReplay.next(false);
        this.refreshReplay = null;
      } else {
        const token = data.data.attributes.token;
        const auth = this.getAuthentication();
        auth.setToken(token);
        this.setAuthentication(auth);
        refreshReplay.next(true);
        this.refreshReplay = null;
      }
    });


    return refreshReplay;
  }

  /**
   * This function calls the http get function of angular to get a specified ressource.
   * The returned value is always an observable<JSON>
   * @param url url of get-operation
   * @return Observable<JSON>
   */
  public get(url: String): ReplaySubject<any> {
    const getReplay = new ReplaySubject(1);

    const headers = new Headers();
    this.createAuthorizationHeader(headers);
    this.http.get(this.baseUrl.toString() + url.toString(), {
      headers: headers
    }).map((res: Response) => {
      if (res['_body']) {
        return res.json();
      } else {
        const ret = {};
        ret['status'] = res.status;
        ret['headers'] = res.headers;
        return ret;
      }
    }).catch((error: any) => {
      // To this otherwise
      return Observable.throw(error);
    }).subscribe(json => {
      getReplay.next(json);
    }, error => {
      // Error handling here
      switch (error.status) {
        case 401:
          if (this.authentication !== null && this.authentication !== undefined) {
            this.refreshToken().subscribe(data => {
              if (data) {
                this.get(url).subscribe(jj => {
                  getReplay.next(jj);
                });
              } else {
                getReplay.next(new Error401());
              }
            });
          } else {
            getReplay.next(new Error401());
          }
          break;
        case 403: getReplay.next(new Error403());
          break;
        case 404: getReplay.next(new Error404());
          break;
        case 409: getReplay.next(new Error409());
          break;
        case 422: // getReplay.next(new Error422());
          const e = new Error422();
          const body = JSON.parse(error._body);
          e.addErrors(body.errors[0].meta.errors);
          getReplay.next(e);
          break;
        default: getReplay.next(new BasicError(error.status, 'undefined', '' + error.status));
      }
    });

    return getReplay;
  }

  /**
   * Corresponding post call of angular.
   * @param url url of post-operation
   * @param data data to be send to the api
   * @return Observable<JSON>
   */
  public post(url: String, data: any): ReplaySubject<any> {
    const postReplay = new ReplaySubject(1);

    const headers = new Headers();
    this.createAuthorizationHeader(headers);
    this.http.post(this.baseUrl.toString() + url.toString(), data, {
      headers: headers
    }).map((res: Response) => {
      if (res['_body']) {
        return res.json();
      } else {
        const ret = {};
        ret['status'] = res.status;
        ret['headers'] = res.headers;
        return ret;
      }
    }).catch((error: any) => {
      // To this otherwise (Errorhandling must be implemented)
      return Observable.throw(error);
    }).subscribe(json => {
      postReplay.next(json);
    }, error => {
      // Error handling here
      switch (error.status) {
        case 401:
          if (this.authentication !== null && this.authentication !== undefined) {
            this.refreshToken().subscribe(dd => {
              if (dd) {
                this.post(url, data).subscribe(jj => {
                  postReplay.next(jj);
                });
              } else {
                postReplay.next(new Error401());
              }
            });
          } else {
            postReplay.next(new Error401());
          }
          break;
        case 403: postReplay.next(new Error403());
          break;
        case 404: postReplay.next(new Error404());
          break;
        case 409: postReplay.next(new Error409());
          break;
        case 422: // postReplay.next(new Error422());
          const e = new Error422();
          const body = JSON.parse(error._body);
          e.addErrors(body.errors[0].meta.errors);
          postReplay.next(e);
          break;
        default: postReplay.next(new BasicError(error.status, 'undefined', '' + error.status));
      }
    });

    return postReplay;
  }

  /**
   * This function triggers the put method on a given resource
   * @param url url of resource to be loaded
   * @param data data to be send with it
   */
  public put(url: String, data: any): ReplaySubject<any> {
    const putReplay = new ReplaySubject(1);

    const headers = new Headers();
    this.createAuthorizationHeader(headers);
    this.http.put(this.baseUrl.toString() + url.toString(), data, {
      headers: headers
    }).map((res: Response) => {
      if (res['_body']) {
        return res.json();
      } else {
        return res.status;
      }
    }).catch((error: any) => {
      // To this otherwise (Errorhandling must be implemented)
      return Observable.throw(error);
    }).subscribe(json => {
      putReplay.next(json);
    }, error => {
      // Error handling here
      switch (error.status) {
        case 401:
          if (this.authentication !== null && this.authentication !== undefined) {
            this.refreshToken().subscribe(dd => {
              if (data) {
                this.put(url, data).subscribe(jj => {
                  putReplay.next(jj);
                });
              } else {
                putReplay.next(new Error401());
              }
            });
          } else {
            putReplay.next(new Error401());
          }
          break;
        case 403: putReplay.next(new Error403());
          break;
        case 404: putReplay.next(new Error404());
          break;
        case 409: putReplay.next(new Error409());
          break;
        case 422: // putReplay.next(new Error422());
          const e = new Error422();
          const body = JSON.parse(error._body);
          e.addErrors(body.errors[0].meta.errors);
          putReplay.next(e);
          break;
        default: putReplay.next(new BasicError(error.status, 'undefined', '' + error.status));
      }
    });

    return putReplay;
  }

  /**
   * This is the patch function to be triggered from angular http
   * @param url url of resource to be patched
   * @param data data to be patched
   */
  public patch(url: String, data: any): ReplaySubject<any> {
    const patchReplay = new ReplaySubject(1);

    const headers = new Headers();
    this.createAuthorizationHeader(headers);
    this.http.patch(this.baseUrl.toString() + url.toString(), data, {
      headers: headers
    }).map((res: Response) => {
      if (res['_body']) {
        return res.json();
      } else {
        return res.status;
      }
    }).catch((error: any) => {
      // To this otherwise (Errorhandling must be implemented)
      return Observable.throw(error);
    }).subscribe(json => {
      patchReplay.next(json);
    }, error => {
      // Error handling here
      switch (error.status) {
        case 401:
          if (this.authentication !== null && this.authentication !== undefined) {
            this.refreshToken().subscribe(dd => {
              if (dd) {
                this.patch(url, data).subscribe(jj => {
                  patchReplay.next(jj);
                });
              } else {
                patchReplay.next(new Error401());
              }
            });
          } else {
            patchReplay.next(new Error401());
          }
          break;
        case 403: patchReplay.next(new Error403());
          break;
        case 404: patchReplay.next(new Error404());
          break;
        case 409: patchReplay.next(new Error409());
          break;
        case 422: // patchReplay.next(new Error422());
          const e = new Error422();
          const body = JSON.parse(error._body);
          e.addErrors(body.errors[0].meta.errors);
          patchReplay.next(e);
          break;
        default: patchReplay.next(new BasicError(error.status, 'undefined', '' + error.status));
      }
    });

    return patchReplay;
  }


  /**
   * This function calls the http delete function of angular to get a specified ressource.
   * The returned value is always an observable<JSON>
   * @param url url of get-operation
   * @return Observable<JSON>
   */
  public delete(url: String, data: any = null): ReplaySubject<any> {
    const getReplay = new ReplaySubject(1);
    const headers = new Headers();
    this.createAuthorizationHeader(headers);
    let req = null;

    if (data !== null) {
      req = this.http.delete(this.baseUrl.toString() + url.toString(), {
        headers: headers,
        body: data
      })
    } else {
      req = this.http.delete(this.baseUrl.toString() + url.toString(), {
        headers: headers
      });
    }

    req.map((res: Response) => {
      if (res['_body']) {
        return res.json();
      } else {
        return res.status;
      }
    }).catch((error: any) => {
      // To this otherwise
      return Observable.throw(error);
    }).subscribe(json => {
      getReplay.next(json);
    }, error => {
      // Error handling here
      switch (error.status) {
        case 401:
          if (this.authentication !== null && this.authentication !== undefined) {
            this.refreshToken().subscribe(dd => {
              if (dd) {
                this.get(url).subscribe(jj => {
                  getReplay.next(jj);
                });
              } else {
                getReplay.next(new Error401());
              }
            });
          } else {
            getReplay.next(new Error401());
          }
          break;
        case 403: getReplay.next(new Error403());
          break;
        case 404: getReplay.next(new Error404());
          break;
        case 409: getReplay.next(new Error409());
          break;
        case 422: // getReplay.next(new Error422());
          const e = new Error422();
          const body = JSON.parse(error._body);
          e.addErrors(body.errors[0].meta.errors);
          getReplay.next(e);
          break;
        default: getReplay.next(new BasicError(error.status, 'undefined', '' + error.status));
      }
    });

    return getReplay;
  }

}



// WEBPACK FOOTER //
// C:/Users/andyw/Documents/Uni/Master/Masterprojekt/Angular/src/app/services/communication.service.ts